import axios from 'axios';
import { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader } from '../../../../components/Loader/Loader';
import { Modal } from '../../../../components/Modal/Modal';
import { axiosInstance } from '../../../../helpers/axios';
import { useFetchRubrics } from '../../../../hooks/useFetchRubrics';
import { IRubric } from '../../../../types/types';
import styles from './RubricList.module.scss';

// бэкенд присылает причину отказа в поле title: занятое название,
// рубрика с материалами
const getError = (error: unknown, fallback: string): string => {
    const detail = axios.isAxiosError(error)
        ? error.response?.data?.title
        : undefined;
    if (Array.isArray(detail)) {
        return String(detail[0]);
    }
    return typeof detail === 'string' ? detail : fallback;
};

export const RubricList = () => {
    const { rubrics, isLoading, refetch } = useFetchRubrics();
    const [newTitle, setNewTitle] = useState<string>('');
    const [editing, setEditing] = useState<IRubric | null>(null);
    const [removing, setRemoving] = useState<IRubric | null>(null);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const addRubric = async (event: FormEvent) => {
        event.preventDefault();
        if (!newTitle.trim()) {
            return;
        }
        setIsSaving(true);
        try {
            await axiosInstance.post('/v1/rubric/', { title: newTitle });
            setNewTitle('');
            await refetch();
            toast.success('Рубрика добавлена');
        } catch (error) {
            toast.error(getError(error, 'Не получилось добавить рубрику'));
        } finally {
            setIsSaving(false);
        }
    };

    const renameRubric = async (event: FormEvent) => {
        event.preventDefault();
        if (!editing || !editing.title.trim()) {
            return;
        }
        setIsSaving(true);
        try {
            await axiosInstance.put(`/v1/rubric/${editing.id}/`, {
                title: editing.title,
            });
            setEditing(null);
            await refetch();
            toast.success('Название изменено');
        } catch (error) {
            toast.error(getError(error, 'Не получилось переименовать рубрику'));
        } finally {
            setIsSaving(false);
        }
    };

    const removeRubric = async () => {
        if (!removing) {
            return;
        }
        setIsSaving(true);
        try {
            await axiosInstance.delete(`/v1/rubric/${removing.id}/`);
            setRemoving(null);
            await refetch();
            toast.success('Рубрика удалена');
        } catch (error) {
            toast.error(getError(error, 'Не получилось удалить рубрику'));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={styles.rubrics}>
            <h2 className={styles.rubrics__header}>Рубрики «Полезного»</h2>
            <p className={styles.rubrics__note}>
                Из этих рубрик сотрудник выбирает рубрику материала, по ним же
                читатели фильтруют список на сайте.
            </p>

            <form className={styles.add} onSubmit={addRubric}>
                <input
                    className={styles.add__field}
                    placeholder="Название рубрики"
                    maxLength={64}
                    value={newTitle}
                    onChange={(event) => setNewTitle(event.target.value)}
                />
                <button
                    className={styles.add__button}
                    type="submit"
                    disabled={!newTitle.trim() || isSaving}
                >
                    Добавить
                </button>
            </form>

            {isLoading ? <Loader /> : null}

            <ul className={styles.list}>
                {rubrics.map((rubric) => (
                    <li key={rubric.id} className={styles.row}>
                        {editing?.id === rubric.id ? (
                            <form className={styles.row__edit} onSubmit={renameRubric}>
                                <input
                                    className={styles.add__field}
                                    maxLength={64}
                                    value={editing.title}
                                    autoFocus
                                    onChange={(event) =>
                                        setEditing({
                                            ...editing,
                                            title: event.target.value,
                                        })
                                    }
                                />
                                <button
                                    className={styles.add__button}
                                    type="submit"
                                    disabled={!editing.title.trim() || isSaving}
                                >
                                    Сохранить
                                </button>
                                <button
                                    className={styles.row__button}
                                    type="button"
                                    onClick={() => setEditing(null)}
                                >
                                    Отмена
                                </button>
                            </form>
                        ) : (
                            <>
                                <span className={styles.row__title}>
                                    {rubric.title}
                                </span>
                                <button
                                    className={styles.row__button}
                                    type="button"
                                    onClick={() => setEditing(rubric)}
                                >
                                    Переименовать
                                </button>
                                <button
                                    className={styles.row__button}
                                    type="button"
                                    onClick={() => setRemoving(rubric)}
                                >
                                    Удалить
                                </button>
                            </>
                        )}
                    </li>
                ))}
            </ul>

            {removing ? (
                <Modal>
                    <div className={styles.modal}>
                        <h3 className={styles.modal__header}>
                            Удалить рубрику «{removing.title}»?
                        </h3>
                        <p className={styles.modal__note}>
                            Рубрику с материалами удалить нельзя: сначала
                            переведите их в другую рубрику.
                        </p>
                        <div className={styles.modal__buttons}>
                            <button
                                className={styles.add__button}
                                type="button"
                                disabled={isSaving}
                                onClick={removeRubric}
                            >
                                Удалить
                            </button>
                            <button
                                className={styles.row__button}
                                type="button"
                                onClick={() => setRemoving(null)}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                </Modal>
            ) : null}
        </div>
    );
};
